import { useCallback, useEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager, type ModalFC } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { useFeature } from '@/features/feature-flags';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { getDeviceSetting, setDeviceSetting } from '@/utils/localStorage';

import {
  WHATS_NEW_EXPIRES_AT,
  WHATS_NEW_VERSION,
  whatsNewContent,
} from './whats-new-content';
import {
  type WhatsNewFeature,
  type WhatsNewModalProps,
} from './whats-new.types';

const SLIDE_DURATION_MS = 5000;

type ProgressBarsProps = {
  total: number;
  currentIndex: number;
  progressRef: React.RefObject<number>;
  onNavigate: (index: number) => void;
};

const ProgressBars = ({
  total,
  currentIndex,
  progressRef,
  onNavigate,
}: ProgressBarsProps) => {
  const fillRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    let raf: number;
    const tick = () => {
      const progress = progressRef.current;
      fillRefs.current.forEach((el, i) => {
        if (!el) return;
        if (i < currentIndex) {
          el.style.width = '100%';
        } else if (i > currentIndex) {
          el.style.width = '0%';
        } else {
          el.style.width = `${progress * 100}%`;
        }
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [currentIndex, progressRef]);

  return (
    <XStack className="w-full items-center justify-center gap-1.5">
      {Array.from({ length: total }, (_, i) => (
        <button
          key={i}
          onClick={() => onNavigate(i)}
          aria-label={`Go to feature ${i + 1}`}
          className="h-1 w-8 overflow-hidden rounded-full bg-white/30 transition-colors hover:bg-white/50"
        >
          <div
            ref={(el) => {
              fillRefs.current[i] = el;
            }}
            className="h-full rounded-full bg-white"
          />
        </button>
      ))}
    </XStack>
  );
};

type ImagePanelProps = {
  feature: WhatsNewFeature;
  total: number;
  currentIndex: number;
  progressRef: React.RefObject<number>;
  onNavigate: (index: number) => void;
};

const ImagePanel = ({
  feature,
  total,
  currentIndex,
  progressRef,
  onNavigate,
}: ImagePanelProps) => {
  const [hovered, setHovered] = useState(false);
  const hasMultiple = total > 1;

  return (
    <YStack
      className="relative w-1/2 overflow-hidden max-sm:order-first max-sm:h-[11.25rem] max-sm:w-full max-sm:shrink-0"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {feature.image}

      {hasMultiple && (
        <>
          <XStack className="absolute bottom-8 left-0 z-10 w-full">
            <ProgressBars
              total={total}
              currentIndex={currentIndex}
              progressRef={progressRef}
              onNavigate={onNavigate}
            />
          </XStack>

          <XStack
            // Touch has no hover, so the next-feature arrow stays visible
            className={cn(
              'pointer-events-none absolute inset-0 items-center justify-end px-3 transition-opacity',
              hovered
                ? 'opacity-100'
                : 'opacity-0 touch:opacity-100 max-sm:opacity-100'
            )}
          >
            <button
              className="pointer-events-auto flex size-9 items-center justify-center rounded-full bg-black text-white transition-colors hover:bg-black/80"
              aria-label="Next feature"
              onClick={() => onNavigate((currentIndex + 1) % total)}
            >
              <Icons.chevronRight className="size-4" />
            </button>
          </XStack>
        </>
      )}
    </YStack>
  );
};

type FeatureContentProps = {
  feature: WhatsNewFeature;
  onClose: () => void;
};

const FeatureContent = ({ feature, onClose }: FeatureContentProps) => {
  const handlePrimaryCta = () => {
    trackEvent('WHATS_NEW_PRIMARY_CTA_CLICKED', {
      feature: feature.title,
      version: WHATS_NEW_VERSION,
    });
    markSeen();
    onClose();
    feature.primaryCta.onClick();
  };

  const handleSecondaryCta = () => {
    trackEvent('WHATS_NEW_SECONDARY_CTA_CLICKED', {
      feature: feature.title,
      version: WHATS_NEW_VERSION,
    });
    markSeen();
    onClose();
    feature.secondaryCta?.onClick();
  };

  return (
    <YStack className="w-1/2 justify-between p-7 max-sm:w-full max-sm:shrink-0 max-sm:gap-6 max-sm:p-6">
      <YStack className="gap-8 pr-2 max-sm:gap-6 max-sm:pr-0">
        <YStack className="gap-4">
          <span className="bg-accent text-accent-foreground-subtle w-fit rounded-md px-2 py-0.5 text-xs font-medium">
            {feature.badge ?? 'New'}
          </span>
          <Dialog.Title className="whitespace-pre-line font-serif text-3xl leading-tight text-foreground max-sm:text-[1.75rem]">
            {feature.title}
          </Dialog.Title>
        </YStack>

        <YStack className="gap-5">
          {feature.bullets.map((bullet, i) => (
            <XStack key={i} className="gap-3">
              <span className="text-foreground-muted mt-1.5 shrink-0">
                {bullet.icon}
              </span>
              <span className="text-foreground-muted">{bullet.text}</span>
            </XStack>
          ))}
        </YStack>
      </YStack>

      <YStack className="shrink-0 gap-3">
        <Button
          variant="default"
          size="lg"
          className="border-0 bg-foreground text-background shadow-none hover:enabled:bg-foreground/90 hover:enabled:text-background"
          onClick={handlePrimaryCta}
        >
          {feature.primaryCta.label}
        </Button>
        {feature.secondaryCta && (
          <Button variant="outline" size="lg" onClick={handleSecondaryCta}>
            {feature.secondaryCta.label}
          </Button>
        )}
      </YStack>
    </YStack>
  );
};

const useAutoAdvance = (total: number) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const progressRef = useRef(0);
  const startTimeRef = useRef(Date.now());
  const rafRef = useRef<number>(0);
  const tickRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setCurrentIndex((i) => (i >= total ? Math.max(total - 1, 0) : i));
  }, [total]);

  const navigate = useCallback((index: number) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setCurrentIndex(index);
    progressRef.current = 0;
    startTimeRef.current = Date.now();
    if (tickRef.current) {
      rafRef.current = requestAnimationFrame(tickRef.current);
    }
  }, []);

  useEffect(() => {
    if (total <= 1) return;

    const tick = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const fraction = Math.min(elapsed / SLIDE_DURATION_MS, 1);
      progressRef.current = fraction;

      if (fraction >= 1) {
        setCurrentIndex((i) => (i + 1) % total);
        progressRef.current = 0;
        startTimeRef.current = Date.now();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    tickRef.current = tick;
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      tickRef.current = null;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [total]);

  return { currentIndex, progressRef, navigate };
};

export const WhatsNewModal: ModalFC<WhatsNewModalProps> = ({
  open,
  onOpenChange,
  features,
}) => {
  const { currentIndex, progressRef, navigate } = useAutoAdvance(
    features.length
  );
  const feature = features[currentIndex];
  const ctaClickedRef = useRef(false);

  if (!feature) {
    if (open) onOpenChange(false);
    return null;
  }

  const handleClose = () => {
    ctaClickedRef.current = true;
    onOpenChange(false);
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          if (!ctaClickedRef.current) {
            trackEvent('WHATS_NEW_DISMISSED', {
              feature: feature.title,
              version: WHATS_NEW_VERSION,
            });
          }
          markSeen();
        }
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        data-testid="whats-new-dialog"
        className="h-[40rem] max-w-4xl gap-0 overflow-hidden border-0 p-0 max-sm:h-auto max-sm:max-h-[calc(100dvh-2rem)] max-sm:overflow-y-auto"
      >
        <XStack className="size-full max-sm:h-auto max-sm:flex-col">
          <FeatureContent feature={feature} onClose={handleClose} />
          <ImagePanel
            feature={feature}
            total={features.length}
            currentIndex={currentIndex}
            progressRef={progressRef}
            onNavigate={navigate}
          />
        </XStack>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const STORAGE_KEY = 'whats-new:seen-version';

const markSeen = () => {
  setDeviceSetting(STORAGE_KEY, WHATS_NEW_VERSION);
};

type ShouldShowWhatsNewParams = {
  seen: boolean;
  contentLength: number;
  now: number;
  expiresAt: number;
};

export function shouldShowWhatsNew({
  seen,
  contentLength,
  now,
  expiresAt,
}: ShouldShowWhatsNewParams): boolean {
  return !seen && contentLength > 0 && now < expiresAt;
}

const useWhatsNew = () => {
  const seen = getDeviceSetting(STORAGE_KEY) === WHATS_NEW_VERSION;

  const show = useCallback((features: WhatsNewFeature[]) => {
    trackEvent('WHATS_NEW_SHOWN', {
      features: features.map((f) => f.title),
      version: WHATS_NEW_VERSION,
    });
    ModalsManager.show(WhatsNewModal, { features }, { unique: true });
  }, []);

  return { seen, show };
};

export const WhatsNewProvider = () => {
  const { seen, show } = useWhatsNew();
  const hasFeature = useFeature();

  const features = whatsNewContent.filter(
    (f) => !f.requiresFeature || hasFeature(f.requiresFeature)
  );

  useEffect(() => {
    if (
      shouldShowWhatsNew({
        seen,
        contentLength: features.length,
        now: Date.now(),
        expiresAt: WHATS_NEW_EXPIRES_AT.getTime(),
      })
    ) {
      show(features);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen, show, features.length]);

  return null;
};
